import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Handshake, Loader2, TrendingUp, CheckCircle2, XCircle, CircleDot,
    IndianRupee, MapPin, ArrowRight,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { CHANNEL_PARTNER_SOURCED_LEADS_URL } from "../../constants/api";
import ToastMsg from "../common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import { getStageBadgeClass } from "../../utils/leadStageBadge";
import { formatDate } from "../../utils/datetime";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

/** Every lead one channel partner introduced, and what became of them.
 *
 *  Shown as a tab on a partner's own detail page. The summary tiles count the
 *  partner's whole book while the table below is paginated — the API is
 *  deliberately built that way, because a partner page reporting "12 leads"
 *  because twelve is one page of them would be worse than showing no number.
 */
export default function PartnerSourcedLeadsPanel({ partnerId }) {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [stageCounts, setStageCounts] = useState({});
    const [stageFilter, setStageFilter] = useState("");
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(true);

    const load = useCallback((opts = {}) => {
        if (!partnerId) return;
        const pg = opts.pg !== undefined ? opts.pg : page;
        const sf = opts.sf !== undefined ? opts.sf : stageFilter;
        setLoading(true);
        fetch(CHANNEL_PARTNER_SOURCED_LEADS_URL, {
            partner_id: partnerId, page_no: pg, page_size: 25,
            ...(sf && { lead_stage: sf }),
        })
            .then(res => {
                if (res?.status === "success") {
                    setRows(res.data || []);
                    setSummary(res.summary || null);
                    // Stage chips come from the unfiltered book, so picking one
                    // does not make the others vanish from the row of chips.
                    if (!sf) setStageCounts(res.stage_counts || {});
                    setPages(res.no_of_page || 1);
                } else {
                    ToastMsg("error", res?.message || "Could not load sourced leads");
                }
            })
            .catch(() => ToastMsg("error", "Could not load sourced leads"))
            .finally(() => setLoading(false));
    }, [partnerId, page, stageFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load({ pg: 1, sf: "" }); setPage(1); }, [partnerId]); // eslint-disable-line react-hooks/exhaustive-deps

    const pickStage = (stage) => {
        const next = stageFilter === stage ? "" : stage;
        setStageFilter(next);
        setPage(1);
        load({ pg: 1, sf: next });
    };

    if (loading && !summary) {
        return (
            <div className="flex items-center justify-center py-12 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                <span className="text-xs">Loading sourced leads…</span>
            </div>
        );
    }

    const tiles = [
        { label: "Leads Sent",  value: summary?.leads ?? 0,  icon: Handshake,   tone: "text-purple-600" },
        { label: "Won",         value: summary?.won ?? 0,    icon: CheckCircle2, tone: "text-green-600" },
        { label: "Open",        value: summary?.open ?? 0,   icon: CircleDot,   tone: "text-blue-600" },
        { label: "Lost",        value: summary?.lost ?? 0,   icon: XCircle,     tone: "text-red-500" },
    ];

    return (
        <div className="space-y-4">
            {/* ── summary tiles ── */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {tiles.map(t => (
                    <div key={t.label} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                        <div className="flex items-center gap-1.5 mb-1">
                            <t.icon className={cn("h-3.5 w-3.5", t.tone)} />
                            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{t.label}</span>
                        </div>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">{t.value}</p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                        <TrendingUp className="h-3.5 w-3.5 text-indigo-600" />
                        <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Conversion</span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {summary?.conversion_rate_pct ?? 0}%
                    </p>
                    <p className="text-[10px] text-gray-400 mt-0.5">of decided leads</p>
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                        <IndianRupee className="h-3.5 w-3.5 text-green-600" />
                        <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Won Value</span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{money(summary?.won_value)}</p>
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                        <IndianRupee className="h-3.5 w-3.5 text-blue-600" />
                        <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Open Value</span>
                    </div>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{money(summary?.open_value)}</p>
                </div>
            </div>

            {/* ── stage chips ── */}
            {Object.keys(stageCounts).length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                    <button
                        onClick={() => pickStage("")}
                        className={cn("px-2.5 py-1 rounded-full text-[10px] font-medium border transition-colors",
                            !stageFilter
                                ? "bg-gray-700 text-white border-gray-800"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700")}
                    >
                        All · {summary?.leads ?? 0}
                    </button>
                    {Object.entries(stageCounts).map(([stage, count]) => (
                        <button key={stage} onClick={() => pickStage(stage)}
                            className={cn("px-2.5 py-1 rounded-full text-[10px] font-medium border transition-colors",
                                stageFilter === stage
                                    ? "bg-indigo-600 text-white border-indigo-700"
                                    : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700")}
                        >
                            {stage} · {count}
                        </button>
                    ))}
                </div>
            )}

            {/* ── leads table ── */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                                <th className="px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Lead</th>
                                <th className="px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Stage</th>
                                <th className="px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Owner</th>
                                <th className="px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 text-right">Deal Value</th>
                                <th className="px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Created</th>
                                <th className="px-3 py-2 w-8" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-10 text-xs text-gray-400 dark:text-gray-500">
                                        {stageFilter
                                            ? `No leads at ${stageFilter}`
                                            : "No leads attributed to this partner yet. Set a lead's source to “Channel Partner” and pick this partner to see it here."}
                                    </td>
                                </tr>
                            ) : rows.map(r => (
                                <tr key={r.id}
                                    onClick={() => navigate(`/customers/customerdetails?id=${r.id}`)}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors">
                                    <td className="px-3 py-2 max-w-[200px]">
                                        <p className="font-medium text-gray-800 dark:text-gray-200 truncate">{r.customer_name || "—"}</p>
                                        {r.city && (
                                            <p className="flex items-center gap-0.5 text-[10px] text-gray-400 mt-0.5">
                                                <MapPin className="h-2.5 w-2.5" />{r.city}
                                            </p>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        {r.lead_stage ? (
                                            <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-medium", getStageBadgeClass(r.lead_stage))}>
                                                {r.lead_stage}
                                            </span>
                                        ) : "—"}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 max-w-[120px] truncate">
                                        {r.assigned_to_name || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                        {r.deal_value ? money(r.deal_value) : "—"}
                                    </td>
                                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                        {r.created_date ? formatDate(r.created_date) : "—"}
                                    </td>
                                    <td className="px-3 py-2 text-gray-300">
                                        <ArrowRight className="h-3 w-3" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {pages > 1 && (
                    <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
                        <span className="text-[10px] text-gray-500">Page {page} of {pages}</span>
                        <div className="flex items-center gap-1">
                            <button disabled={page === 1 || loading}
                                onClick={() => { const p = page - 1; setPage(p); load({ pg: p }); }}
                                className="px-2 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 text-gray-600 dark:text-gray-300">
                                Prev
                            </button>
                            <button disabled={page === pages || loading}
                                onClick={() => { const p = page + 1; setPage(p); load({ pg: p }); }}
                                className="px-2 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 text-gray-600 dark:text-gray-300">
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
